'use client';

import { c as _c } from "react/compiler-runtime";
import './field.scss';
import { ReactNode, useEffect, useId } from 'react';
import { type FieldError, type RegisterOptions, type UseFormRegisterReturn, type UseFormReturn, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useUnmount from 'react-use/lib/useUnmount';
import { ArkIcon, ArkIconProps } from '@sola/ark-icons/lib/ark-icon.tsx';
import { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { isDevelopmentEnvironment } from '@sola/shared-utils/misc.ts';
import { cn } from '../../utils/helpers.ts';
import { TooltipInfo } from '../tooltip-info/tooltip-info.tsx';
import { TooltipContentProps } from '../tooltip/tooltip.tsx';

/**
 * Returns field error element id based on field id.
 */
export const getErrorElId = (fieldId: string) => `err-${fieldId}`;
export interface FieldOutput {
  /**
   * Register options are only required within a form.
   */
  controlProps: Partial<UseFormRegisterReturn> & {
    id: string;
    /**
     * Only used when not in a form and checkbox needs to be controlled.
     */
    checked?: boolean;
    /**
     * Initial value.
     */
    defaultValue?: RegisterOptions['value'];
    /**
     * Only used when not in a form and field needs to be controlled.
     */
    value?: string | number | string[];
  };
  /**
   * Only available within a form.
   */
  error?: FieldError | undefined;
  /**
   * Only available within a form.
   */
  formMethods?: Omit<UseFormReturn, 'register' | 'setFocus'>;
}
export interface FieldProps extends Omit<RegisterOptions, 'label'> {
  children: (props: FieldOutput) => React.ReactNode;
  controlRef?: React.MutableRefObject<HTMLElement>;
  /**
   * This shouldn't be used for fields within a <Form/>, instead, you should be setting default values within the Form component's options prop.
   */
  defaultValue?: FieldOutput['controlProps']['defaultValue'];
  isFocused?: boolean;
  name: string;
  fieldClasses?: string;
  checked?: boolean;
}
export interface FieldWithFormProps extends FieldProps {
  id: string;
  context: UseFormReturn;
}
export const FieldWithForm: React.FC<FieldWithFormProps> = ({
  children,
  context,
  controlRef,
  defaultValue,
  id,
  isFocused,
  name,
  fieldClasses,
  shouldUnregister,
  ...fieldOptions
}) => {
  if (!name) {
    throw new Error('A field is missing a name');
  }
  if (defaultValue && isDevelopmentEnvironment) {
    console.error(`Anathema! Looks like you tried setting defaultValue directly on a field within a <Form/>. Instead you should be setting all default values within the Form options prop, which maps to args from RHF's useForm(). See https://react-hook-form.com/docs/useform#defaultValues`);
  }
  const {
    register,
    setFocus,
    ...methods
  } = context;
  const {
    errors
  } = useFormState();
  const {
    t
  } = useTranslation(['forms']);

  // Make the component controlled if value is passed as an option.
  // TODO: We should remove this, but for now leaving it until we have tests for backwards compatbility.
  useEffect(() => {
    if (!fieldOptions.value) {
      return;
    }
    methods.setValue(name, fieldOptions.value);
  }, [fieldOptions.value]);

  // Possibly unregister when unmounted.
  useUnmount(() => {
    if (shouldUnregister) {
      methods.unregister(name);
    }
  });

  // Programatically set focus.
  useEffect(() => {
    if (isFocused) {
      setFocus(name);
    }
  }, [isFocused]);

  // Set default required error message if necessary.
  const fieldError = errors[name];
  if (fieldError && !fieldError.message && fieldError.type === 'required') {
    fieldError.message = t('forms:errors.requiredField');
  }

  // Register field and pass necessary props to component.
  const {
    ref,
    ...registerProps
  } = register(name, fieldOptions as RegisterOptions);
  const renderOutput = {
    controlProps: {
      id,
      required: !!fieldOptions.required,
      ...registerProps,
      ref: (e: HTMLElement) => {
        ref(e);
        if (!controlRef) {
          return;
        }
        controlRef.current = e;
      }
    },
    error: fieldError as FieldError | undefined,
    formMethods: methods
  };
  return <div className={cn('Field', {
    'is-required': fieldOptions.required,
    'has-error': fieldError,
    [`${fieldClasses}`]: fieldClasses
  })}>
      {children(renderOutput)}
    </div>;
};

/**
 * @TODO this needs some docs and tidying up.
 */
export const Field: React.FC<FieldProps> = t0 => {
  const $ = _c(4);
  const {
    ...props
  } = t0;
  const id = useId();
  const formContext = useFormContext();
  if (!formContext) {
    return props.children({
      controlProps: {
        id,
        value: props.value,
        checked: props.checked,
        defaultValue: props.defaultValue
      }
    });
  }
  let t1;
  if ($[0] !== formContext || $[1] !== id || $[2] !== props) {
    t1 = <FieldWithForm id={id} context={formContext} {...props} />;
    $[0] = formContext;
    $[1] = id;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export interface FieldWrapperProps extends React.PropsWithChildren {
  className?: string;
  isCentered?: boolean;
}
export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  className,
  children,
  isCentered = true
}) => {
  return <div className={cn('relative flex', isCentered ? 'items-center' : 'items-start', {
    [`${className}`]: !!className
  })}>
      {children}
    </div>;
};
export const FieldErrorIcon: React.FC<{
  error?: FieldError;
}> = t0 => {
  const $ = _c(2);
  const {
    error
  } = t0;
  let t1;
  if ($[0] !== error) {
    t1 = !error ? null : <span className="Field__error-icon"><ArkIcon iconName="alert-circle" size="20" /></span>;
    $[0] = error;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const FieldErrorMessage: React.FC<{
  error?: FieldError;
  id: string;
}> = t0 => {
  const $ = _c(3);
  const {
    error,
    id
  } = t0;
  let t1;
  if ($[0] !== error || $[1] !== id) {
    t1 = !error ? null : <div id={id} className="Field__error mt-snug-lg" role="alert">{error.message as string}</div>;
    $[0] = error;
    $[1] = id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export const FieldTooltipIcon: React.FC<{
  help?: string | ReactNode;
  tooltipFit?: TooltipContentProps['fitContent'];
  tooltipSize?: TooltipContentProps['size'];
}> = t0 => {
  const $ = _c(4);
  const {
    help,
    tooltipFit,
    tooltipSize: t1
  } = t0;
  const tooltipSize = t1 === undefined ? "sm" : t1;
  let t2;
  if ($[0] !== help || $[1] !== tooltipFit || $[2] !== tooltipSize) {
    t2 = !help ? null : <TooltipInfo triggerClassName="Field__tooltip-icon" contentClassName="Field__tooltip" fitContent={tooltipFit} size={tooltipSize} alignOffset={-12} onPointerDownOutside={_temp}>{help}</TooltipInfo>;
    $[0] = help;
    $[1] = tooltipFit;
    $[2] = tooltipSize;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
interface FieldIconProps {
  iconName?: IconNames;
  className?: string;
  label?: string;
  size?: ArkIconProps['size'];
  action?: () => void;
}
export const FieldIconButton: React.FC<FieldIconProps & {
  ref?: React.Ref<HTMLButtonElement>;
}> = t0 => {
  const $ = _c(9);
  const {
    iconName,
    className,
    label,
    action,
    size: t1,
    ref
  } = t0;
  const size = t1 === undefined ? "20" : t1;
  if (!iconName) {
    return null;
  }
  let t2;
  if ($[0] !== className || $[1] !== iconName || $[2] !== size) {
    t2 = <ArkIcon iconName={iconName} className={className} size={size} />;
    $[0] = className;
    $[1] = iconName;
    $[2] = size;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== action || $[5] !== label || $[6] !== ref || $[7] !== t2) {
    t3 = <button className="Field__icon-btn" title={label} aria-label={label} type="button" ref={ref} onClick={action} tabIndex={0}>{t2}</button>;
    $[4] = action;
    $[5] = label;
    $[6] = ref;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
export const FieldIcon: React.FC<FieldIconProps> = props => {
  const {
    iconName,
    className,
    label,
    action,
    size
  } = props;
  if (!iconName) {
    return null;
  }
  if (action) {
    return <FieldIconButton {...props} />;
  }
  return <ArkIcon iconName={iconName} aria-label={label} size={size} className={cn('Field__icon', {
    [`${className}`]: !!className
  })} />;
};
function _temp(e) {
  e.preventDefault();
}